import { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { cn } from 'Utils/cn';
import { cva } from 'class-variance-authority';
import ButtonClear from '../ButtonClear/ButtonClear';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import FloatLabel from '../FloatLabel/FloatLabel';

const inputVariants = cva(
    'w-full flex w-full text-dark-blue rounded-md border border-gray-200 bg-white px-3.5 shadow-sm transition-all placeholder:text-dark-blue/50 focus-visible:outline-none focus-visible:ring-2 ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
    {
        variants: {
            size: {
                normal: 'h-11',
                small: 'h-8 md:text-xs',
                large: 'h-14 md:text-base',
            },
            status: {
                error: 'border-danger',
                highlight: 'border-warning ring-2 focus-visible:ring-2 ring-warning/20 focus-visible:border-warning',
                default: '',
            },
        },
        defaultVariants: {
            size: 'normal',
            status: 'default',
        },
    },
);

const Input = forwardRef(function Input(props, ref) {
    const {
        className,
        type = 'text',
        label,
        size,
        enableClear,
        onClear,
        error,
        highlight,
        onBlur,
        hasError: hasErrorProp,
        prefix,
        suffix,
        ...rest
    } = props;

    const hasError = hasErrorProp || !!error;

    const [isHighlighted, setIsHighlighted] = useState(highlight || false);
    const status = isHighlighted ? 'highlight' : hasError ? 'error' : 'default';

    const affixPadding = (affix, side) => {
        if (!affix) return '';
        const len = typeof affix === 'string' ? affix.length : 2;
        const map =
            side === 'left'
                ? { sm: 'pl-8', md: 'pl-10', lg: 'pl-13' }
                : { sm: 'pr-8', md: 'pr-10', lg: 'pr-13' };
        return len <= 1 ? map.sm : len === 2 ? map.md : map.lg;
    };

    return (
        <div className="relative group w-full">
            <FloatLabel label={label} hasError={hasError} highlight={isHighlighted} />

            <div className="relative">
                {prefix && (
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-dark-blue/60 pointer-events-none z-10">
                        {prefix}
                    </span>
                )}
                <input
                    type={type}
                    className={cn(
                        inputVariants({ size, status, className }),
                        affixPadding(prefix, 'left'),
                        affixPadding(suffix, 'right'),
                        enableClear && 'pr-9',
                    )}
                    ref={ref}
                    onBlur={(e) => {
                        setIsHighlighted(false);
                        onBlur?.(e);
                    }}
                    {...rest}
                />
                {suffix && !enableClear && (
                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-dark-blue/60 pointer-events-none z-10">
                        {suffix}
                    </span>
                )}
                {enableClear && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2">
                        <ButtonClear onClear={onClear} />
                    </span>
                )}
            </div>

            <ErrorMessage message={error} />
        </div>
    );
});

export default Input;

Input.propTypes = {
    className: PropTypes.string,
    type: PropTypes.string,
    size: PropTypes.oneOf(['normal', 'small', 'large']),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    enableClear: PropTypes.bool,
    onClear: PropTypes.func,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    highlight: PropTypes.bool,
    onBlur: PropTypes.func,
    hasError: PropTypes.bool,
    prefix: PropTypes.node,
    suffix: PropTypes.node,
};
